import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { MaterialCategory, MaterialItem } from '@/types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";

interface MaterialInputModalProps {
  category: MaterialCategory | null;
  isOpen: boolean;
  onClose: () => void;
  onAddItem: (item: MaterialItem) => void;
}

const MaterialInputModal: React.FC<MaterialInputModalProps> = ({
  category,
  isOpen,
  onClose,
  onAddItem,
}) => {
  const [wholeInches, setWholeInches] = useState('');
  const [fraction, setFraction] = useState('0');
  const [quantity, setQuantity] = useState('1');

  // Generate fraction options in 1/16 increments
  const fractionOptions = [
    { value: '0', label: '0' },
    { value: '1/16', label: '1/16' },
    { value: '1/8', label: '1/8' },
    { value: '3/16', label: '3/16' },
    { value: '1/4', label: '1/4' },
    { value: '5/16', label: '5/16' },
    { value: '3/8', label: '3/8' },
    { value: '7/16', label: '7/16' },
    { value: '1/2', label: '1/2' },
    { value: '9/16', label: '9/16' },
    { value: '5/8', label: '5/8' },
    { value: '11/16', label: '11/16' },
    { value: '3/4', label: '3/4' },
    { value: '13/16', label: '13/16' },
    { value: '7/8', label: '7/8' },
    { value: '15/16', label: '15/16' }
  ];

  // Convert fraction string to decimal
  const fractionToDecimal = (fractionStr: string): number => {
    if (fractionStr === '0') return 0;

    const [numerator, denominator] = fractionStr.split('/').map(Number);
    return numerator / denominator;
  };

  // Reset form when modal opens with a new category
  useEffect(() => {
    if (isOpen && category) {
      setWholeInches('');
      setFraction('0');
      setQuantity('1');
    }
  }, [isOpen, category]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!category) return;

    // Calculate total length from whole inches and fraction
    const wholeNumber = parseFloat(wholeInches) || 0;
    const fractionDecimal = fractionToDecimal(fraction);
    const totalLength = wholeNumber + fractionDecimal;

    const newItem: MaterialItem = {
      id: uuidv4(),
      categoryId: category.id,
      name: `${category.name}`,
      length: totalLength,
      quantity: parseInt(quantity) || 1,
    };

    onAddItem(newItem);
  };

  if (!category) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add {category.name} Material</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid gap-4">
            <div className="flex gap-4 items-end">
              <div className="flex-1 grid gap-2">
                <Label htmlFor="wholeInches">Inches</Label>
                <Input
                  id="wholeInches"
                  type="number"
                  min="0"
                  value={wholeInches}
                  onChange={(e) => setWholeInches(e.target.value)}
                  placeholder="Whole"
                  required
                />
              </div>

              <div className="flex-1 grid gap-2">
                <Label htmlFor="fraction">Fraction</Label>
                <Select value={fraction} onValueChange={setFraction}>
                  <SelectTrigger
                    id="fraction"
                    className="border-[#4b5563] bg-[#1e293b] hover:bg-[#243447] hover:border-[#60a5fa] transition-colors duration-200"
                  >
                    <SelectValue placeholder="Fraction" />
                  </SelectTrigger>
                  <SelectContent className="border-[#4b5563] bg-[#1e293b]">
                    {fractionOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        className="focus:bg-[#243447] focus:text-white"
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex-1 grid gap-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="Qty"
                  required
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              Add Material
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MaterialInputModal;
